import { Serializer } from "@shared/mirage/helpers/Serializer";
import _ from "lodash";
import { lazy, Suspense, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import EnforcementAppealsSearchState from "./EnforcementAppealsSearchState";
import EnforcementAppealsSimpleSearch from "./EnforcementAppealsSimpleSearch";
import EnforcementAppealsService from "@publicPortal/services/EnforcementAppealsService";
import { EntityTableStateStorage } from "@mirage/components/entityTable/EntityTableStateStorage";
import PublicPortalState from "@publicPortal/utils/InitialState";
import { EnforcementAppealsFilter } from "@shared/publicportal/contracts/EnforcementAppealsFilter";
import { atom, useRecoilState } from "recoil";
import MapFallback from "@mirage/components/MapView/components/MapFallback";
import SearchTypeSwitchingTabs from "@publicPortal/components/advancedSearch/SearchTypeSwitchingTabs";
import SearchActions from "../SearchActions";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { SearchType } from "@shared/publicportal/contracts/Search";
import { PublicPortalDrawProps } from "../Search";
import EnforcementAppealsAdvancedSearch from "@publicPortal/components/advancedSearch/EnforcementAppealsAdvancedSearch";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";

const MapView = lazy(() =>
    import("@mirage/components/MapView/MapView").then((module) => {
        return { default: module.MapView };
    })
);

const service = new EnforcementAppealsService();
const { initialSearchState, initialFilterState } = PublicPortalState.enforcementAppealInitialSearchState();
const enforcementAppealsSearchState = atom({
    key: "enforcementAppealsSearchState",
    default: initialSearchState,
});

const EnforcementAppealsSearch = () => {
    const [state, setState] = useRecoilState<EnforcementAppealsSearchState>(enforcementAppealsSearchState);
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { clientSettings } = sessionValue;
    const navigate = useStableNavigate();
    const pp = new PublicPortalRoutingService();
    const setStateModifier = (modifier: Partial<EnforcementAppealsFilter>) => {
        setState((prev) => {
            return { ...prev, filter: { ...prev.filter, ...modifier } };
        });
    };
    const onSearchTypeChanged = useCallback(
        (newSearchType: SearchType) => {
            setState((prev) => {
                return { ...prev, searchType: newSearchType };
            });
        },
        [setState]
    );

    useEffect(() => {
        service.getFilteringLists().then((filter) => {
            const lists = { decisionTypes: filter.decisionTypes || [], parishes: filter.parishes || [], wards: filter.wards || [] };
            setState((prev) => {
                return { ...prev, ...lists };
            });
        });
    }, []);
    const onClear = () => {
        setState((prev) => {
            return {
                ...prev,
                filter: {
                    ...initialFilterState,
                },
            };
        });
    };
    return (
        <>
            <SearchTypeSwitchingTabs searchType={state.searchType} onSearchTypeChanged={onSearchTypeChanged} />
            {getForm()}
            {formActions()}
        </>
    );

    function mapComponent() {
        const initialGeometry = state.filter.geometry ? (JSON.parse(state.filter.geometry) as GeoJSON.GeoJSON) : undefined;
        return (
            <Suspense fallback={<MapFallback />}>
                <MapView
                    center={[clientSettings.MAP_CENTER_LAT, clientSettings.MAP_CENTER_LONG]}
                    zoom={7}
                    geometries={initialGeometry}
                    drawProps={PublicPortalDrawProps}
                    onChange={(g) => {
                        if (g.drawn) {
                            setStateModifier({ geometry: JSON.stringify(g.drawn.geoJson) });
                        }
                    }}
                    headerText="Spatial Locations"
                    masterLayer={
                        clientSettings
                            ? {
                                  layerUrl: clientSettings.MASTER_LAYER_URL,
                                  attribution: clientSettings.MASTER_LAYER_ATTRIBUTION,
                              }
                            : undefined
                    }
                />
            </Suspense>
        );
    }

    function getForm() {
        switch (state.searchType) {
            case "Simple": {
                return <EnforcementAppealsSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <EnforcementAppealsAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return mapComponent();
            }
            default:
                return null;
        }
    }

    function prepareFilter() {
        switch (state.searchType) {
            case "Simple": {
                return {
                    searchType: state.searchType,
                    appealRef: state.filter.appealRef,
                    noticeRef: state.filter.noticeRef,
                    inspectorateRef: state.filter.inspectorateRef,
                };
            }
            case "Advanced": {
                return { ...state.filter, searchType: state.searchType };
            }
            case "Map": {
                return { searchType: state.searchType, geometry: state.filter.geometry };
            }
            default:
                return null;
        }
    }

    function formActions() {
        return (
            <SearchActions
                onCancelHandler={onClear}
                onSubmitHandler={() => {
                    const filter = prepareFilter();
                    const searchParams = Serializer.toQueryParamsNotEmptyOnly(filter);
                    EntityTableStateStorage.clear("enforcementappeals");
                    navigate(`${pp.SearchRoute("EnforcementAppeals", searchParams)}`);
                }}
            />
        );
    }
};

export default EnforcementAppealsSearch;
